import {
    Box, Typography, Button, MenuItem, Select, Checkbox, ListItemText,
    OutlinedInput, Skeleton,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import DownloadIcon from '@mui/icons-material/Download';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Toast } from 'primereact/toast';
import CustomAxios from "../../utils/CustomAxios";

/**
 * Wallet and Razorpay reporting for the order desk.
 *
 * Two reports, both able to take several hubs at once:
 *   - Razorpay sales: every Razorpay-paid order in the chosen hubs and dates.
 *   - Wallet: one row per customer -- deposited, top-up bonus, deducted on
 *     orders -- and clicking a customer opens that person's wallet-paid orders.
 *
 * Like the user reports screen, the filters name a report the server already
 * knows rather than building a query, and the on-screen table and the download
 * are the same request so the two can never disagree.
 */

/** Must match the REPORTS keys in the backend's user_reports.py. */
const REPORTS = [
    { key: 'razorpay_sales', label: 'Razorpay sales' },
    { key: 'wallet_summary', label: 'Wallet' },
];

/** Reached by clicking a customer row, not from the picker. */
const DRILL_WALLET = 'wallet_orders';

/* ---- palette ------------------------------------------------------------- */
const PINK = '#ED1E79';
const PINK_DK = '#C40E63';
const INK = '#16121A';
const MUTED = '#6E6478';
const FAINT = '#9A92A6';
const LINE = '#ECE6F1';
const CARD = '#FFFFFF';
const PAGE = '#F7F4FA';
const CHIP = '#F4EFF7';

/** Tone used by the KPI tiles: [accent line, soft wash, value colour]. */
const TONE = {
    brand: ['#7A4FE0', '#F1ECFB', '#5B34C4'],
    good: ['#12A368', '#E6F6EF', '#0B7A4E'],
    accent: [PINK, '#FDEBF4', PINK_DK],
    muted: ['#8A93A6', '#EEF1F6', '#5B6472'],
};

/** Status pill colours for the order tables. */
const STATUS_TONE = {
    delivered: ['#0B7A4E', '#E6F6EF'],
    order_picked_up: ['#0B6FA4', '#E4F1FA'],
    accepted: ['#0B6FA4', '#E4F1FA'],
    pending: ['#9A6B06', '#FBF0DA'],
    declined: ['#B23030', '#FBE7E7'],
    cancelled: ['#B23030', '#FBE7E7'],
};

/** Columns shown right-aligned with tabular figures. */
const NUMERIC = [
    'order_id', 'user_id', 'order_amount', 'razorpay_paid', 'paid_from_wallet',
    'deposited', 'bonus_given', 'deducted_orders', 'orders_paid', 'balance',
];
/** Columns rendered as rupee amounts. */
const MONEY = [
    'order_amount', 'razorpay_paid', 'paid_from_wallet',
    'deposited', 'bonus_given', 'deducted_orders', 'balance',
];
/** The figure the table should lean on in each report. */
const EMPHASIS = {
    razorpay_sales: 'order_amount',
    wallet_summary: 'deposited',
    wallet_orders: 'paid_from_wallet',
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const rupee = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

/**
 * The figures above the table, worked out from the rows on screen so they match
 * the table and the download exactly.
 */
function summaryFor(report, rows) {
    const sum = (key) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    if (report === 'razorpay_sales') {
        const orders = rows.filter((r) => r.order_id !== '—').length;
        const gross = sum('order_amount');
        return [
            { label: 'Razorpay orders', value: orders.toLocaleString('en-IN'), tone: 'brand' },
            { label: 'Gross sales', value: rupee(gross), tone: 'good' },
            { label: 'Avg order value', value: rupee(orders ? gross / orders : 0), tone: 'accent' },
        ];
    }
    if (report === 'wallet_summary') {
        return [
            { label: 'Wallet customers', value: rows.length.toLocaleString('en-IN'), tone: 'brand' },
            { label: 'Total deposited', value: rupee(sum('deposited')), tone: 'good' },
            { label: 'Bonus given', value: rupee(sum('bonus_given')), tone: 'accent' },
            { label: 'Deducted on orders', value: rupee(sum('deducted_orders')), tone: 'muted' },
        ];
    }
    if (report === DRILL_WALLET) {
        return [
            { label: 'Wallet orders', value: rows.length.toLocaleString('en-IN'), tone: 'brand' },
            { label: 'Paid from wallet', value: rupee(sum('paid_from_wallet')), tone: 'good' },
            { label: 'Order value', value: rupee(sum('order_amount')), tone: 'accent' },
        ];
    }
    return [];
}

/** A label above a control, so nothing floats over the value. */
function Field({ label, children, grow }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "5px", flex: grow ? "1 1 200px" : "0 0 auto", minWidth: 0 }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: FAINT, pl: "2px" }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}

export function WalletReports() {
    const toast = useRef(null);
    const inventory = useSelector((state) => state.inventory.inventory) || [];

    const [report, setReport] = useState('razorpay_sales');
    /** Several hubs at once. Empty = all hubs. */
    const [hubIds, setHubIds] = useState([]);
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    /** Set by clicking a customer; the crumb above the table clears it. Carries
     *  the whole customer so the heading can name them without a second lookup. */
    const [drill, setDrill] = useState(null);

    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    /** What is actually being shown: the picked report, or the drill-down. */
    const effectiveReport = drill ? DRILL_WALLET : report;

    const params = useMemo(() => {
        const p = { report: effectiveReport };
        if (hubIds.length) p.hub_ids = hubIds.join(',');
        // Empty means all time. Sending a blank would be an invalid date.
        if (start) p.start = start;
        if (end) p.end = end;
        if (effectiveReport === DRILL_WALLET && drill?.user_id) p.user_id = drill.user_id;
        return p;
    }, [effectiveReport, hubIds, start, end, drill]);

    const run = useCallback(async () => {
        setLoading(true);
        try {
            const res = await CustomAxios.get('reports/users/', { params });
            setColumns(res?.data?.columns || []);
            setRows(res?.data?.rows || []);
            setTitle(res?.data?.title || '');
        } catch (e) {
            const detail = e?.response?.data?.error || 'Could not load this report.';
            toast.current?.show({ severity: 'error', summary: 'Report failed', detail, life: 5000 });
            setColumns([]); setRows([]);
        } finally {
            setLoading(false);
        }
    }, [params]);

    // Re-run on any filter change -- these are aggregates, so pressing a Run
    // button to see what you just chose would be ceremony rather than protection.
    useEffect(() => { run(); }, [run]);

    const download = async () => {
        setDownloading(true);
        try {
            const res = await CustomAxios.get('reports/users/', {
                // `export`, not `format`: DRF reserves `format` and answers 404
                // before the view is reached. responseType blob is not optional --
                // the shared axios parses every response as JSON and would corrupt
                // the spreadsheet otherwise.
                params: { ...params, export: 'xlsx' },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `shino-${effectiveReport}-${todayISO()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.current?.show({
                severity: 'error', summary: 'Download failed',
                detail: 'Could not build the spreadsheet.', life: 5000,
            });
        } finally {
            setDownloading(false);
        }
    };

    const cards = useMemo(() => summaryFor(effectiveReport, rows), [effectiveReport, rows]);

    const heading = (c) => String(c).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
    const isNumeric = (c) => NUMERIC.includes(c);
    // Only the wallet summary drills down, and only a customer who actually has
    // wallet-paid orders -- clicking someone who only ever deposited would open
    // an empty list and read as a bug.
    const canDrill = effectiveReport === 'wallet_summary';
    const rowClickable = (r) => canDrill && r.user_id != null && Number(r.orders_paid) > 0;
    const emphCol = EMPHASIS[effectiveReport];
    const visibleCols = columns.filter((c) => c !== 'user_id');

    const statusPill = (value) => {
        const key = String(value).toLowerCase().replace(/\s+/g, '_');
        const [fg, bg] = STATUS_TONE[key] || [MUTED, CHIP];
        return (
            <Box component="span" sx={{
                display: "inline-block", fontSize: "11px", fontWeight: 700, borderRadius: "999px",
                padding: "2px 10px", color: fg, backgroundColor: bg, whiteSpace: "nowrap",
                textTransform: "capitalize", letterSpacing: "0.01em",
            }}>
                {String(value).replace(/_/g, ' ')}
            </Box>
        );
    };

    const cell = (col, row) => {
        const value = row[col];
        if (value === null || value === undefined || value === '') return '—';
        if (col === 'status') return statusPill(value);
        if (MONEY.includes(col)) return rupee(value);
        return String(value);
    };

    /** Hub label by id, for the multi-select. */
    const hubName = (id) => {
        const h = inventory.find((x) => (x.value ?? x.id) === id);
        return h ? (h.name || h.place || `Hub ${id}`) : `Hub ${id}`;
    };

    const renderHubValue = (selected) => {
        if (!selected.length) return <Box component="span" sx={{ color: FAINT }}>All hubs</Box>;
        if (selected.length <= 2) return selected.map(hubName).join(', ');
        return `${selected.length} hubs selected`;
    };

    return (
        <Box sx={{ backgroundColor: PAGE, minHeight: "100%", padding: { xs: "14px", md: "24px" } }}>
            <Toast ref={toast} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: "18px", maxWidth: "1280px", margin: "0 auto" }}>

                {/* ---- header ---- */}
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: "13px", flexShrink: 0,
                        background: `linear-gradient(135deg, ${PINK} 0%, ${PINK_DK} 100%)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 6px 16px rgba(237,30,121,0.30)",
                    }}>
                        <StorefrontOutlinedIcon sx={{ color: "#fff", fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: "22px", fontWeight: 800, letterSpacing: "-0.02em", color: INK, lineHeight: 1.1 }}>
                            Wallet &amp; Razorpay reports
                        </Typography>
                        <Typography sx={{ fontSize: "13px", color: MUTED, marginTop: "3px" }}>
                            Pick one or more hubs and a date range — the numbers on screen are the numbers in the download.
                        </Typography>
                    </Box>
                </Box>

                {/* ---- filters ---- */}
                <Box sx={{
                    display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-end",
                    backgroundColor: CARD, border: `1px solid ${LINE}`, borderRadius: "16px",
                    padding: "16px 18px", boxShadow: "0 1px 2px rgba(22,18,26,0.04)",
                }}>
                    <Field label="Report">
                        <Box sx={{
                            display: "inline-flex", backgroundColor: PAGE, border: `1px solid ${LINE}`,
                            borderRadius: "11px", padding: "3px", gap: "3px",
                        }}>
                            {REPORTS.map((r) => {
                                const on = report === r.key;
                                return (
                                    <Button key={r.key} disableElevation onClick={() => { setReport(r.key); setDrill(null); }}
                                        sx={{
                                            textTransform: "none", minWidth: 0, fontSize: "13px", fontWeight: 700,
                                            padding: "7px 16px", borderRadius: "8px", whiteSpace: "nowrap",
                                            color: on ? "#fff" : MUTED,
                                            background: on ? `linear-gradient(135deg, ${PINK}, ${PINK_DK})` : "transparent",
                                            boxShadow: on ? "0 3px 9px rgba(237,30,121,0.28)" : "none",
                                            "&:hover": { background: on ? `linear-gradient(135deg, ${PINK}, ${PINK_DK})` : "#fff" },
                                        }}>
                                        {r.label}
                                    </Button>
                                );
                            })}
                        </Box>
                    </Field>

                    <Field label="Hubs" grow>
                        <Select
                            multiple
                            displayEmpty
                            value={hubIds}
                            onChange={(e) => setHubIds(
                                // MUI gives a string when autofilled; normalise to an array.
                                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                            )}
                            input={(
                                <OutlinedInput
                                    startAdornment={<StorefrontOutlinedIcon sx={{ fontSize: 18, color: FAINT, mr: "8px", ml: "-2px" }} />}
                                    endAdornment={hubIds.length ? (
                                        <Box component="span" sx={{
                                            mr: "26px", fontSize: 11, fontWeight: 800, color: PINK_DK,
                                            backgroundColor: "#FDEBF4", borderRadius: "999px", padding: "1px 8px",
                                        }}>{hubIds.length}</Box>
                                    ) : null}
                                />
                            )}
                            renderValue={renderHubValue}
                            sx={{
                                backgroundColor: "#fff", fontSize: 13.5, height: 42, borderRadius: "11px",
                                minWidth: 220,
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: LINE },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D9CEE6" },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PINK, borderWidth: "1.5px" },
                            }}
                            MenuProps={{ PaperProps: { style: { maxHeight: 320, borderRadius: 12, marginTop: 6 } } }}
                        >
                            {inventory.length === 0 && (
                                <MenuItem disabled sx={{ fontSize: 13 }}>No hubs available</MenuItem>
                            )}
                            {inventory.map((h) => {
                                const id = h.value ?? h.id;
                                const on = hubIds.indexOf(id) > -1;
                                return (
                                    <MenuItem key={id} value={id} sx={{ fontSize: 13.5, borderRadius: "8px", mx: "4px", my: "1px" }}>
                                        <Checkbox checked={on} size="small" sx={{ color: "#C9BFD6", p: "4px", mr: "4px", "&.Mui-checked": { color: PINK } }} />
                                        <ListItemText primary={h.name || h.place || `Hub ${id}`} primaryTypographyProps={{ fontSize: 13.5, fontWeight: on ? 700 : 400 }} />
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </Field>

                    <Field label="From">
                        <Box component="input" type="date" value={start} onChange={(e) => setStart(e.target.value)}
                            sx={dateInputSx} />
                    </Field>
                    <Field label="To">
                        <Box component="input" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                            sx={dateInputSx} />
                    </Field>

                    <Box sx={{ flex: "1 1 auto" }} />

                    <Button variant="contained" disableElevation startIcon={<DownloadIcon />}
                        onClick={download} disabled={downloading || loading || !rows.length}
                        sx={{
                            textTransform: "none", fontSize: 13.5, fontWeight: 700, height: 42,
                            color: "#fff", borderRadius: "11px", padding: "0 20px",
                            background: `linear-gradient(135deg, ${INK}, #2A2430)`,
                            boxShadow: "0 4px 12px rgba(22,18,26,0.20)",
                            "&:hover": { background: "#000" },
                            "&.Mui-disabled": { background: "#D9D2E0", color: "#fff" },
                        }}>
                        {downloading ? 'Preparing…' : 'Download .xlsx'}
                    </Button>
                </Box>

                {/* ---- KPI tiles ---- */}
                {cards.length > 0 && (
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px" }}>
                        {cards.map((c) => {
                            const [bar, wash, valCol] = TONE[c.tone] || TONE.accent;
                            return (
                                <Box key={c.label} sx={{
                                    position: "relative", overflow: "hidden", backgroundColor: CARD,
                                    border: `1px solid ${LINE}`, borderRadius: "16px", padding: "16px 18px",
                                    boxShadow: "0 1px 2px rgba(22,18,26,0.04)",
                                }}>
                                    <Box sx={{ position: "absolute", right: -18, top: -18, width: 76, height: 76, borderRadius: "50%", backgroundColor: wash }} />
                                    <Box sx={{ position: "relative" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: "3px", backgroundColor: bar }} />
                                            <Typography sx={{ fontSize: "11px", letterSpacing: "0.06em", textTransform: "uppercase", color: MUTED, fontWeight: 700 }}>
                                                {c.label}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: "27px", fontWeight: 800, letterSpacing: "-0.03em", marginTop: "8px", fontVariantNumeric: "tabular-nums", color: valCol, lineHeight: 1.1 }}>
                                            {c.value}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* ---- table ---- */}
                <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "16px", overflow: "hidden", backgroundColor: CARD, boxShadow: "0 1px 2px rgba(22,18,26,0.04)" }}>
                    <Box sx={{
                        padding: "13px 18px", borderBottom: `1px solid ${LINE}`,
                        display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap",
                        backgroundColor: "#FCFBFD",
                    }}>
                        {effectiveReport === DRILL_WALLET ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: 14, flexWrap: "wrap" }}>
                                <Button onClick={() => setDrill(null)} startIcon={<ArrowBackIosNewIcon sx={{ fontSize: "13px !important" }} />}
                                    sx={{
                                        textTransform: "none", fontSize: 13, fontWeight: 700, color: PINK, padding: "4px 10px",
                                        borderRadius: "8px", minWidth: 0, backgroundColor: "#FDEBF4",
                                        "&:hover": { backgroundColor: "#FBDCEC" },
                                    }}>
                                    All customers
                                </Button>
                                <Box component="span" sx={{ color: "#D9CEE6" }}>/</Box>
                                <Box component="span" sx={{ fontWeight: 800, color: INK }}>{drill?.customer || '—'}</Box>
                                <Box component="span" sx={{ fontSize: 12, color: MUTED }}>· wallet-paid orders</Box>
                            </Box>
                        ) : (
                            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: INK, letterSpacing: "-0.01em" }}>{title || '—'}</Typography>
                        )}
                        <Box sx={{
                            fontSize: 12, fontWeight: 700, color: MUTED, backgroundColor: CHIP,
                            borderRadius: "999px", padding: "3px 11px",
                        }}>
                            {loading ? 'Loading…' : `${rows.length} row${rows.length === 1 ? '' : 's'}`}
                        </Box>
                    </Box>

                    {loading ? (
                        <Box sx={{ padding: "8px 18px 18px" }}>
                            {[...Array(6)].map((_, i) => (
                                <Box key={i} sx={{ display: "flex", gap: "16px", padding: "11px 0", borderBottom: i < 5 ? `1px solid ${LINE}` : "none" }}>
                                    {[...Array(5)].map((_, j) => (
                                        <Skeleton key={j} variant="rounded" height={14} sx={{ flex: j === 0 ? 2 : 1, borderRadius: "6px", bgcolor: "#F0EBF5" }} />
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    ) : !rows.length ? (
                        <Box sx={{ padding: "54px 24px", textAlign: "center" }}>
                            <Box sx={{
                                width: 52, height: 52, borderRadius: "50%", backgroundColor: CHIP, margin: "0 auto 14px",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <StorefrontOutlinedIcon sx={{ color: FAINT, fontSize: 26 }} />
                            </Box>
                            <Typography sx={{ color: INK, fontSize: 14, fontWeight: 700 }}>
                                Nothing to show yet
                            </Typography>
                            <Typography sx={{ color: MUTED, fontSize: 13, marginTop: "4px" }}>
                                {effectiveReport === DRILL_WALLET
                                    ? 'No wallet-paid orders for this customer in this date range.'
                                    : 'No rows match these hubs and dates. Try widening the range.'}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ overflowX: "auto", maxHeight: "60vh" }}>
                            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: "13.5px", minWidth: "640px" }}>
                                <thead>
                                    <tr>
                                        {visibleCols.map((c) => (
                                            <th key={c} style={{
                                                position: "sticky", top: 0, zIndex: 1, backgroundColor: "#FCFBFD",
                                                textAlign: isNumeric(c) ? "right" : "left",
                                                padding: "11px 16px", borderBottom: `1.5px solid ${LINE}`,
                                                fontSize: "10.5px", letterSpacing: ".07em", textTransform: "uppercase",
                                                color: MUTED, whiteSpace: "nowrap", fontWeight: 800,
                                            }}>{heading(c)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => {
                                        const clickable = rowClickable(r);
                                        return (
                                            <tr key={i}
                                                style={{
                                                    transition: "background 120ms",
                                                    cursor: clickable ? "pointer" : "default",
                                                    backgroundColor: i % 2 ? "#FCFBFD" : "#fff",
                                                }}
                                                onClick={clickable ? () => setDrill({ user_id: r.user_id, customer: r.customer }) : undefined}
                                                onMouseEnter={(e) => { if (clickable) e.currentTarget.style.backgroundColor = "#FBEFF6"; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = i % 2 ? "#FCFBFD" : "#fff"; }}>
                                                {visibleCols.map((c) => {
                                                    const emph = c === emphCol;
                                                    return (
                                                        <td key={c} style={{
                                                            textAlign: isNumeric(c) ? "right" : "left",
                                                            padding: "11px 16px", borderBottom: `1px solid ${LINE}`,
                                                            fontVariantNumeric: isNumeric(c) ? "tabular-nums" : "normal",
                                                            fontWeight: emph ? 800 : (c === 'customer' ? 600 : 400),
                                                            color: r[c] === 0 ? "#B6AFC0" : (emph ? INK : "#3A3340"),
                                                            whiteSpace: "nowrap",
                                                        }}>
                                                            {c === 'customer' && clickable ? (
                                                                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", color: PINK_DK, fontWeight: 700 }}>
                                                                    {cell(c, r)}
                                                                    <ChevronRightIcon sx={{ fontSize: 16, ml: "2px" }} />
                                                                </Box>
                                                            ) : cell(c, r)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

/** Native date input, styled to sit level with the hub select and the button. */
const dateInputSx = {
    height: 42, border: `1px solid ${LINE}`, borderRadius: "11px", padding: "0 13px",
    fontSize: 13.5, color: INK, backgroundColor: "#fff", fontFamily: "inherit",
    minWidth: 150, outline: "none", colorScheme: "light",
    "&:hover": { borderColor: "#D9CEE6" },
    "&:focus": { borderColor: PINK, borderWidth: "1.5px", padding: "0 12.5px" },
    "&::-webkit-calendar-picker-indicator": { cursor: "pointer", opacity: 0.55 },
};

export default WalletReports;
